import { useQuery } from "@tanstack/react-query";

import { getEmployerIdentity } from "../api/services/employerJobs";
import { useSuperApp } from "./useSuperApp";
import { useAccountRole } from "../context/RoleContext";

/**
 * Whether the signed-in person is a Diil admin.
 *
 * Deliberately NOT `useEmployerCompany().admin`, even though that reads the same field off
 * the same endpoint. That hook is gated on `accountRole === "employer"`, because for an
 * ordinary employer the question is "which company do I belong to" and a jobseeker has no
 * business asking it. An admin belongs to no company and can be in either role at the time
 * — so through that hook they would read `admin: false` and be bounced off their own screen.
 *
 * Same query key, though, so the two share one cached response rather than each fetching
 * /diil/employer/me.
 *
 * This only decides what the client draws. The real gate is server-side: every /diil/admin
 * endpoint checks the caller's phone against an active ADMIN row in `users` and answers 403.
 */
export const useIsDiilAdmin = () => {
  const { isAuthenticated, isAuthLoading } = useSuperApp();
  const { accountRole } = useAccountRole();

  const query = useQuery({
    queryKey: ["employerIdentity"],
    enabled: isAuthenticated,
    // Whether someone is staff does not change minute to minute, and /diil/employer/me is
    // not cheap — describeCaller() behind it runs several lookups. This hook is mounted on
    // pages every signed-in visitor sees (the search page asks it, to decide whether to
    // offer the CV bank tab), so without this it would re-run on each of those visits for
    // everybody, to tell almost all of them "no".
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      // getEmployerIdentity never throws — it returns an envelope — so a failure has to be
      // turned into one here for react-query to record it as an error rather than as
      // "not an admin". Same shape as useEmployerCompany's queryFn, for the same reason.
      const res = await getEmployerIdentity();
      if (!res?.success) {
        const err = new Error(res?.error || "Хандах эрх шалгахад алдаа гарлаа");
        err.status = res?.status ?? 0;
        throw err;
      }
      return res.data || {};
    },
  });

  const isAdmin = !!query.data?.admin;

  return {
    isAdmin,
    /**
     * An admin who is currently in their admin profile — the employer role, which the role
     * switcher names "Админ" for these accounts. The staff tools' links (CV сан, Админ
     * анкет, the search page's Профайл tab) follow this rather than `isAdmin`: an admin
     * reading the site as a jobseeker sees what a jobseeker sees. The admin pages
     * themselves still open on `isAdmin`, so a link or a reload never bounces them.
     */
    inAdminMode: isAdmin && accountRole === "employer",
    /**
     * True until we actually know. Callers must not render a redirect on this — an admin
     * refreshing their own page would be thrown to the home screen before the answer lands.
     */
    isLoading: isAuthLoading || (isAuthenticated && query.isLoading),
    /** Signed out, or the identity call failed — either way, not established as an admin. */
    isAuthenticated,
    /**
     * The identity call itself failed. Distinct from `isAdmin: false`, and a screen that
     * only admins can open has to tell them apart: one means "not for you", the other means
     * "we could not ask" — and answering the second with a redirect turns a dropped request
     * into what looks like revoked access.
     */
    error: query.error || null,
    refetch: query.refetch,
  };
};

export default useIsDiilAdmin;
